import axios from 'axios';
import { TENANT_HEADER, TENANT_STORAGE_KEY } from '../constants/tenancy';
import {
  canQueueMutation,
  enqueueMutation,
  getCachedResponse,
  installOfflineListeners,
  isMutationRequest,
  saveCachedResponse,
  syncQueuedMutations,
} from './offlineStore';

const defaultApiUrl =
  typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:5080/api'
    : '/api';

const apiUrl = process.env.REACT_APP_API_URL || defaultApiUrl;

const api = axios.create({
    baseURL: apiUrl
});

const NO_AUTH_ROUTES = ['/tenants'];
const PUBLIC_AUTH_ROUTES = ['/login', '/auth/login', '/auth/forgot-password', '/auth/reset-password'];

const isPublicTenantRoute = (url) => {
  const normalized = url.split('?')[0].replace(/\/+$/, '');
  return normalized === '/tenants';
};

const isTenantDiscoveryRoute = (url) => {
  const normalized = url.split('?')[0].replace(/\/+$/, '');
  return normalized === '/tenants' || normalized === '/tenants/me';
};

api.interceptors.request.use(config => {
  const url = config.url || '';
  const tenantId = localStorage.getItem(TENANT_STORAGE_KEY);

  if (NO_AUTH_ROUTES.some(() => isPublicTenantRoute(url))) {
    return config;
  }

  if (PUBLIC_AUTH_ROUTES.some(route => url.includes(route))) {
    if (tenantId && !url.includes('/auth/login')) {
      config.headers[TENANT_HEADER] = tenantId;
    }
    return config;
  }

  if (tenantId) {
    config.headers[TENANT_HEADER] = tenantId;
  }

  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

api.interceptors.response.use(
  async response => {
    await saveCachedResponse(response.config, response);
    return response;
  },
  async error => {
    const config = error.config || {};

    if (!error.response && !navigator.onLine) {
      if (canQueueMutation(config)) {
        const queued = await enqueueMutation(config);
        return {
          data: {
            offlineQueued: true,
            localId: queued.localId,
            message: 'Operação armazenada neste dispositivo e aguardando sincronização.',
          },
          status: 202,
          statusText: 'Armazenada offline',
          headers: {},
          config,
          offline: true,
        };
      }

      const cached = await getCachedResponse(config);
      if (cached) {
        return {
          data: cached.data,
          status: cached.status || 200,
          statusText: 'Dados salvos no dispositivo',
          headers: {},
          config,
          offline: true,
        };
      }

      if (isMutationRequest(config)) {
        const message = 'Esta operação exige conexão com a internet e não foi armazenada.';
        error.message = message;
        error.response = { status: 503, data: { message }, config };
      }
    }

    if (!error.response) {
      return Promise.reject(error);
    }

    const url = error.config?.url ?? '';
    const path = window.location.pathname;

    if (url.includes('static') || url.includes('.css') || url.includes('.js')) {
      return Promise.reject(error);
    }

    if (isTenantDiscoveryRoute(url) || PUBLIC_AUTH_ROUTES.some(route => url.includes(route))) {
      return Promise.reject(error);
    }

    if (error.response.status === 401 && path !== '/login') {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }

    if (error.response.status === 402 && path !== '/login') {
      const message = error.response.data?.message || 'Sistema bloqueado por mensalidade vencida.';
      window.alert(message);
    }

    return Promise.reject(error);
  }
);

installOfflineListeners(api.defaults.baseURL);
syncQueuedMutations(api.defaults.baseURL);

export default api;
